import * as React from "react";
import * as model from "./model";
import "./resume.css";

export interface JobProps {
  jobs: model.JobExperience[];
}

export interface EducationProps {
  education: model.EducationExperience[];
}

export interface PublicationProps {
  publications: model.Publication[];
}

export const Resume: React.FunctionComponent<model.Resume> = (props) => (
  <div className="resume">
    <div className="contact-row">
      <h1 className="name">
        <a href="resume.pdf" title="Open PDF" target="_blank">
          {props.name}
        </a>
      </h1>
      <Contact {...props.contact} />
    </div>
    <Statement name={props.name} statement={props.statement} />
    <Employment jobs={props.jobs} />
    <Education education={props.education} />
    <Publications publications={props.publications} />
  </div>
);

const Contact: React.FunctionComponent<model.Contact> = (props) => (
  <div className="contact">
    <div className="address">
      {props.address.split("\n").map((item, index) => (
        <div key={index}>{item}</div>
      ))}
    </div>
    <div className="email">
      <a href={`mailto:${props.email}`}>{props.email}</a>
    </div>
    <div className="phone">{props.phone}</div>
    <div>
      <a href={`https://${props.github}`}>{props.github}</a>
    </div>
    <div>
      <a href={`https://${props.blog}`}>{props.blog}</a>
    </div>
  </div>
);

const Statement: React.FunctionComponent<model.Statement> = (props) => (
  <div className="statement">
    {props.statement.split("\n").map((item, index) => (
      <div key={index}>{item}</div>
    ))}
  </div>
);

interface ExpanderProps {
  hideAfter?: number;
  expandAll?: boolean;
  more: string;
}

interface ExpanderState {
  hideAfter: number;
}

const Expander: React.FunctionComponent<ExpanderProps> = (props) => {
  const [state, setState] = React.useState<ExpanderState>({
    hideAfter: props.hideAfter || 1,
  });

  const expand = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (props.expandAll) {
      setState({ hideAfter: 0 });
    } else {
      setState({ hideAfter: state.hideAfter + 1 });
    }
  };

  const array = Array.isArray(props.children)
    ? props.children
    : [props.children];

  const more = state.hideAfter && state.hideAfter < array.length;
  const visible = more ? array.slice(0, state.hideAfter) : array;
  const expando = more ? (
    <div className="expando" onClick={expand}>
      {props.more}
    </div>
  ) : null;

  return (
    <div>
      {visible}
      {expando}
    </div>
  );
};

const Education: React.FunctionComponent<EducationProps> = (props) => (
  <div className="section education">
    <h2>Education</h2>
    <Expander more="more education &gt;&gt;">
      {props.education.map((el, i) => (
        <EducationItem key={i} {...el} />
      ))}
    </Expander>
  </div>
);

const EducationItem: React.FunctionComponent<model.EducationExperience> = (
  props
) => (
  <div className="educationItem row">
    <div className="institution">
      {props.institution.split("\n").map((item, index) => (
        <div key={index}>{item}</div>
      ))}
    </div>
    <div className="dates">{props.dates}</div>
    <div className="course">{props.course}</div>
  </div>
);

const Employment: React.FunctionComponent<JobProps> = (props) => (
  <div className="section employment">
    <h2>Professional Experience</h2>
    <Expander hideAfter={2} more="more jobs &gt;&gt;">
      {props.jobs.map((el, i) => (
        <Job key={i} {...el} />
      ))}
    </Expander>
  </div>
);

const Job: React.FunctionComponent<model.JobExperience> = (props) => (
  <div className="job">
    <div className="row">
      <div className="employer">{props.employer}</div>
      <div className="date">
        {props.from} – {props.to || "Present"}
      </div>
    </div>
    <div className="row">
      <div className="title">{props.title}</div>
      <div className="technologies">{props.technologies}</div>
    </div>
    <div className="row">
      <div className="location">{props.location}</div>
    </div>
    <div className="row jobExperience">
      <Expander hideAfter={2} more="more details &gt;&gt;&gt;">
        {props.experience.map((el, i) => (
          <Experience key={i} {...el} />
        ))}
      </Expander>
    </div>
  </div>
);

const Experience: React.FunctionComponent<model.JobExperienceItem> = (
  props
) => (
  <div className="experience">
    <div className="subtitle">{props.subtitle}</div>
    <div className="dates">{props.dates}</div>
    {props.body.map((item, index) => (
      <ExperienceItem key={index} data={item} />
    ))}
  </div>
);

interface ExperienceItemProps {
  data: string;
}

const ExperienceItem: React.FunctionComponent<ExperienceItemProps> = (
  props
) => {
  const parts = props.data.split("\n");
  if (parts.length == 0) {
    return null;
  } else if (parts.length == 1) {
    return <div>{parts[0]}</div>;
  } else {
    return (
      <div>
        {parts[0]}
        <ul>
          {parts.slice(1).map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      </div>
    );
  }
};

const Publications: React.FunctionComponent<PublicationProps> = (props) => (
  <div className="section publications">
    <h2>Publications, Patents</h2>
    <Expander hideAfter={1} more="show all" expandAll={true}>
      {props.publications.map((el, i) => (
        <Publication key={i} {...el} />
      ))}
    </Expander>
  </div>
);

const Publication: React.FunctionComponent<model.Publication> = (props) => {
  const title = props.uri ? (
    <a href={props.uri} target="_blank">
      {props.title}
    </a>
  ) : (
    props.title
  );
  return (
    <div className="publication">
      <div className="title">{title}</div>
      <div className="publisher">{props.publisher}</div>
      <div className="date">{props.date}</div>
    </div>
  );
};
